import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import posthog from 'posthog-js'
import { useState } from 'react'

import { Dialog, DialogContent } from '@/components/ui/dialog'
import {
  claimFunnelAccount,
  claimNeedsCode,
  resendVerificationCode,
  verifyCode,
} from '@/models/funnelSession'
import { EmailCaptureStep } from './EmailCaptureStep'
import { VerificationCodeStep } from './VerificationCodeStep'

interface Contact {
  email: string
  /** Always sent, blank included — a blank is how the backend hears "no phone". */
  phone: string
}

function errorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: string } | undefined)?.detail
    if (detail) return detail
  }
  return fallback
}

/**
 * "Get quotes for free" — the visitor has their estimates and wants to act on
 * them, so this is where we finally ask who they are. An address we have never
 * seen drops them straight into onboarding; the email is proved later, inline in
 * the chat. An address that already has a Cai account still needs a code here,
 * since without one it would sign them into somebody else's account.
 */
export function VerifyEmailDialog({
  open,
  onOpenChange,
  sessionId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  sessionId: string
}) {
  // The address a code actually went to. Kept while the visitor edits it, so
  // correcting a typo means fixing one character rather than retyping.
  const [sentTo, setSentTo] = useState<Contact | null>(null)
  const [codeEmail, setCodeEmail] = useState('')
  const [phoneEnding, setPhoneEnding] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)

  const claim = useMutation({
    mutationFn: (value: Contact) => claimFunnelAccount(sessionId, value),
    onSuccess: (result, value) => {
      posthog.capture('insure_funnel_email_submitted', { funnel_session_id: sessionId })
      if (!claimNeedsCode(result)) {
        // Replace, not push: back from the app must not land on this spent funnel page.
        window.location.replace(result.loginUrl)
        return
      }
      setSentTo(value)
      setCodeEmail(result.email)
      setPhoneEnding(result.phoneEnding ?? null)
      setIsEditing(false)
    },
  })
  const resend = useMutation({
    mutationFn: () => resendVerificationCode(sessionId),
    onSuccess: (result) => setPhoneEnding(result.phoneEnding ?? null),
  })
  const verify = useMutation({
    mutationFn: (value: string) => verifyCode(sessionId, value),
    onSuccess: (loginUrl) => {
      window.location.replace(loginUrl)
    },
  })

  // Keeps the button spinning until the redirect lands, so it never flashes back to an idle form.
  const isClaiming = claim.isPending || (claim.isSuccess && !claimNeedsCode(claim.data))
  const claimError = claim.isError
    ? errorMessage(claim.error, "We couldn't set up your account. Please try again.")
    : undefined
  const resendError = resend.isError
    ? errorMessage(resend.error, "We couldn't send that code. Please try again.")
    : undefined

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Top-aligned on phones: centred, the keyboard opened over the fields and the button. */}
      <DialogContent className="max-sm:mt-2 max-sm:mb-auto">
        {sentTo && !isEditing ? (
          <VerificationCodeStep
            email={codeEmail || sentTo.email}
            phoneEnding={phoneEnding}
            isVerifying={verify.isPending || verify.isSuccess}
            isResending={resend.isPending}
            errorMessage={
              verify.isError
                ? errorMessage(verify.error, 'That code did not work. Please try again.')
                : resendError
            }
            onSubmit={(code) => verify.mutate(code)}
            onResend={() => {
              verify.reset()
              resend.mutate()
            }}
            onChangeEmail={() => {
              verify.reset()
              resend.reset()
              setIsEditing(true)
            }}
          />
        ) : (
          <EmailCaptureStep
            initialEmail={sentTo?.email ?? ''}
            initialPhone={sentTo?.phone ?? ''}
            isSending={isClaiming}
            errorMessage={claimError}
            onSubmit={(value) => claim.mutate(value)}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
